/**
 * 邮件日历组件 — 首页 Dashboard 月视图电子日历
 *
 * 功能特性：
 * - macOS 风格月视图：6×7 网格（周日起始），带细分隔线，今天红色圆点高亮
 * - 日期格内展示被打上「日历」标签的邮件标题（一行一条，超长省略号截断）
 * - 每天最多展示 3 条，超出以「+N」提示
 * - 日期数字旁展示当日新建客户图标 + 数量，悬停浮层列出客户名单，
 *   点击跳转客户列表并按该创建日期过滤（customers?created=YYYY-MM-DD）
 * - 悬停邮件条目弹出自定义浮层，展示标题/发件人/收件人基础信息
 * - 点击邮件条目跳转到邮件管理页并打开该邮件详情（emails?email_id=xxx）
 * - 支持 ‹ › 切换月份和「今天」快捷回跳，切月重新按网格可见范围调接口查询
 * - 卡片和日期网格使用固定高度，切换月份时不会因内容数量变化而跳动
 *
 * 数据来源：`dashboardApi.calendar(start, end)`，时间范围为当前网格
 * 首末可见日期（浏览器本地时区计算，覆盖前后月补位格）。
 *
 * 主要导出：`CrmEmailCalendar`（自定义元素 `<crm-email-calendar>`）
 */
import { html, css } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { BaseElement } from '../base-element.js';
import { dashboardApi } from '../api.js';
import './loading-overlay.js';
import { router } from '../router.js';
import type { CalendarEmail, CalendarCustomer } from '../types.js';

/** 每个日期格最多展示的邮件条数，超出部分以「+N」折叠 */
const MAX_VISIBLE_ITEMS = 3;

/** 星期表头（周日起始，与 macOS 日历一致） */
const WEEKDAY_LABELS = ['日', '一', '二', '三', '四', '五', '六'];

/** 日期格数据：单元格日期、是否属于当前查看月、当日邮件列表、当日新建客户 */
interface CalendarCell {
  date: Date;
  inMonth: boolean;
  items: CalendarEmail[];
  customers: CalendarCustomer[];
}

/** 悬停浮层数据：目标邮件 + 视口坐标 + 浮层朝向（条目上方/下方） */
interface TooltipState {
  email: CalendarEmail;
  x: number;
  y: number;
  above: boolean;
}

/** 客户浮层数据：当日新建客户列表 + 视口坐标 + 浮层朝向 */
interface CustomerTooltipState {
  customers: CalendarCustomer[];
  x: number;
  y: number;
  above: boolean;
}

/** 客户浮层最多列出的客户条数，超出以「+N」折叠（完整名单点击跳转客户列表查看） */
const MAX_TOOLTIP_CUSTOMERS = 10;

@customElement('crm-email-calendar')
export class CrmEmailCalendar extends BaseElement {
  /**
   * host 布局样式：块级、相对定位和固定高度，避免月份数据变化触发网格重新分配行高。
   */
  static styles = css`
    :host {
      display: block;
      position: relative;
      height: 900px;
    }

    .calendar-date-grid {
      flex: 0 0 760px;
      height: 760px;
      grid-template-rows: repeat(6, minmax(0, 1fr));
    }

    @media (max-width: 1023px) {
      :host {
        height: 840px;
      }

      .calendar-date-grid {
        flex-basis: 700px;
        height: 700px;
      }
    }
  `;

  /** 当前查看的年份（完整年份，如 2026） */
  @state() private viewYear = new Date().getFullYear();
  /** 当前查看的月份（0-11） */
  @state() private viewMonth = new Date().getMonth();
  /** 当前网格范围内的「日历」标签邮件 */
  @state() private items: CalendarEmail[] = [];
  /** 当前网格范围内创建的客户 */
  @state() private customers: CalendarCustomer[] = [];
  /** 当前月份日历数据请求状态 */
  @state() private isLoading = false;
  /** 悬停浮层状态，null 表示隐藏 */
  @state() private tooltip: TooltipState | null = null;
  /** 客户浮层状态，null 表示隐藏 */
  @state() private customerTooltip: CustomerTooltipState | null = null;
  /** 日历请求序号，仅允许最后一次月份切换请求更新网格。 */
  private _loadRequestId = 0;

  /** 组件挂载后加载当前月份的日历邮件 */
  connectedCallback(): void {
    super.connectedCallback();
    void this.loadItems();
  }

  /**
   * 计算当前月视图网格的首末日期（含前后月补位格，共 42 格）
   * @returns 网格起始日期与结束日期（均为本地时间的 Date 对象）
   */
  private gridBounds(): { start: Date; end: Date } {
    const first = new Date(this.viewYear, this.viewMonth, 1);
    // 周日起始：向前回退 first.getDay() 天得到网格首日
    const start = new Date(this.viewYear, this.viewMonth, 1 - first.getDay());
    const end = new Date(start.getFullYear(), start.getMonth(), start.getDate() + 41);
    return { start, end };
  }

  /**
   * 按当前网格范围加载「日历」标签邮件和新建客户
   * 失败时静默清空（仪表盘容错降级，不弹 toast）
   */
  private async loadItems(): Promise<void> {
    const requestId = ++this._loadRequestId;
    this.isLoading = true;
    try {
      const { start, end } = this.gridBounds();
      const startTs = Math.floor(new Date(start.getFullYear(), start.getMonth(), start.getDate()).getTime() / 1000);
      const endTs = Math.floor(new Date(end.getFullYear(), end.getMonth(), end.getDate(), 23, 59, 59).getTime() / 1000);
      const res = await dashboardApi.calendar(startTs, endTs);
      if (requestId !== this._loadRequestId) return;
      this.items = res.items;
      this.customers = res.customers || [];
    } catch {
      if (requestId === this._loadRequestId) {
        this.items = [];
        this.customers = [];
      }
    } finally {
      if (requestId === this._loadRequestId) this.isLoading = false;
    }
  }

  /** 切换到上一个月并重新加载数据 */
  private prevMonth(): void {
    const d = new Date(this.viewYear, this.viewMonth - 1, 1);
    this.viewYear = d.getFullYear();
    this.viewMonth = d.getMonth();
    this.tooltip = null;
    this.customerTooltip = null;
    void this.loadItems();
  }

  /** 切换到下一个月并重新加载数据 */
  private nextMonth(): void {
    const d = new Date(this.viewYear, this.viewMonth + 1, 1);
    this.viewYear = d.getFullYear();
    this.viewMonth = d.getMonth();
    this.tooltip = null;
    this.customerTooltip = null;
    void this.loadItems();
  }

  /** 跳回当前月份并重新加载数据 */
  private goToday(): void {
    const now = new Date();
    this.viewYear = now.getFullYear();
    this.viewMonth = now.getMonth();
    this.tooltip = null;
    this.customerTooltip = null;
    void this.loadItems();
  }

  /**
   * 显示邮件条目的悬停浮层
   * 以条目在视口中的位置计算浮层坐标：默认显示在条目上方，上方空间不足时改到下方
   * @param e - 鼠标事件（currentTarget 为邮件条目按钮）
   * @param email - 目标邮件
   */
  private showTooltip(e: MouseEvent, email: CalendarEmail): void {
    const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
    const width = 384;
    // 横向钳制在视口内（左右各留 8px 边距）
    const x = Math.min(Math.max(8, rect.left), window.innerWidth - width - 8);
    const above = rect.top > 180;
    this.tooltip = { email, x, y: above ? rect.top - 8 : rect.bottom + 8, above };
  }

  /** 隐藏悬停浮层 */
  private hideTooltip(): void {
    this.tooltip = null;
  }

  /** 点击邮件条目：跳转到邮件管理页并打开该邮件详情 */
  private openEmail(email: CalendarEmail): void {
    this.tooltip = null;
    router.navigate(`emails?email_id=${email.id}`);
  }

  /**
   * 显示当日新建客户图标的悬停浮层
   * 坐标计算与邮件浮层一致：默认显示在上方，空间不足时改到下方
   * @param e - 鼠标事件（currentTarget 为客户图标按钮）
   * @param customers - 当日创建的客户列表
   */
  private showCustomerTooltip(e: MouseEvent, customers: CalendarCustomer[]): void {
    const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
    const width = 320;
    const x = Math.min(Math.max(8, rect.left), window.innerWidth - width - 8);
    const above = rect.top > 220;
    this.customerTooltip = { customers, x, y: above ? rect.top - 8 : rect.bottom + 8, above };
  }

  /** 隐藏客户悬停浮层 */
  private hideCustomerTooltip(): void {
    this.customerTooltip = null;
  }

  /**
   * 点击客户图标：跳转客户列表并按当天创建日期过滤
   * @param date - 日期格对应的日期
   */
  private openCustomersForDay(date: Date): void {
    this.customerTooltip = null;
    const pad = (n: number) => String(n).padStart(2, '0');
    router.navigate(`customers?created=${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`);
  }

  /**
   * 格式化发件人/收件人为可读文本（有名字用「名字 <邮箱>」，否则仅邮箱）
   * @param addr - 地址对象 {name, email}
   * @returns 可读文本
   */
  private formatAddr(addr: { name: string; email: string }): string {
    return addr?.name ? `${addr.name} <${addr.email}>` : addr?.email || '';
  }

  /**
   * 构建当前月视图的 42 个日期格数据
   * 将邮件按本地日期（年-月-日）分组后挂到对应格子，
   * 新建客户按创建日期同样分组挂到格子
   * @returns 42 个日期格数组
   */
  private buildCells(): CalendarCell[] {
    const { start } = this.gridBounds();
    const dayKey = (d: Date) => `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
    // 按本地日期分组邮件
    const byDay = new Map<string, CalendarEmail[]>();
    for (const item of this.items) {
      const key = dayKey(new Date(item.email_date * 1000));
      const list = byDay.get(key) || [];
      list.push(item);
      byDay.set(key, list);
    }
    // 按创建日期分组客户
    const customersByDay = new Map<string, CalendarCustomer[]>();
    for (const c of this.customers) {
      const key = dayKey(new Date(c.created_at * 1000));
      const list = customersByDay.get(key) || [];
      list.push(c);
      customersByDay.set(key, list);
    }
    const cells: CalendarCell[] = [];
    for (let i = 0; i < 42; i++) {
      const date = new Date(start.getFullYear(), start.getMonth(), start.getDate() + i);
      const key = dayKey(date);
      cells.push({
        date,
        inMonth: date.getMonth() === this.viewMonth,
        items: byDay.get(key) || [],
        customers: customersByDay.get(key) || [],
      });
    }
    return cells;
  }

  /**
   * 渲染悬停浮层（固定定位，展示标题/发件人/收件人）
   * @returns Lit TemplateResult 或空字符串
   */
  private renderTooltip() {
    if (!this.tooltip) return '';
    const { email, x, y, above } = this.tooltip;
    const to = (email.send_to || []).map(r => this.formatAddr(r)).filter(Boolean).join('、') || '-';
    return html`
      <div class="fixed z-50 w-96 max-w-sm bg-surface border border-border rounded-md shadow-lg p-4 text-sm pointer-events-none"
        style="left: ${x}px; top: ${y}px; ${above ? 'transform: translateY(-100%);' : ''}">
        <div class="font-semibold text-text mb-2 leading-snug break-all">${email.subject || '(无主题)'}</div>
        <div class="text-text-muted leading-loose">
          <div><span class="text-text-secondary">发件人：</span>${this.formatAddr(email.send_from) || '-'}</div>
          <div><span class="text-text-secondary">收件人：</span>${to}</div>
        </div>
      </div>
    `;
  }

  /**
   * 渲染当日新建客户悬停浮层（固定定位）
   * 最多列出 MAX_TOOLTIP_CUSTOMERS 条，超出显示「+N」；底部提示点击可跳转客户列表
   * @returns Lit TemplateResult 或空字符串
   */
  private renderCustomerTooltip() {
    if (!this.customerTooltip) return '';
    const { customers, x, y, above } = this.customerTooltip;
    const visible = customers.slice(0, MAX_TOOLTIP_CUSTOMERS);
    return html`
      <div class="fixed z-50 w-80 max-w-sm bg-surface border border-border rounded-md shadow-lg p-4 text-sm pointer-events-none"
        style="left: ${x}px; top: ${y}px; ${above ? 'transform: translateY(-100%);' : ''}">
        <div class="font-semibold text-text mb-2">当日新建客户（${customers.length}）</div>
        <div class="space-y-1">
          ${visible.map(c => html`
            <div class="flex items-baseline gap-2 text-text-muted leading-snug">
              <span class="truncate font-medium text-text">${c.company_name || c.contact_name || '-'}</span>
              ${c.company_name && c.contact_name ? html`<span class="truncate text-xs shrink-0">${c.contact_name}</span>` : ''}
            </div>
          `)}
          ${customers.length > MAX_TOOLTIP_CUSTOMERS
            ? html`<div class="text-xs text-text-muted">+${customers.length - MAX_TOOLTIP_CUSTOMERS} 个客户…</div>`
            : ''}
        </div>
        <div class="mt-2 pt-2 border-t border-border text-xs text-text-muted">点击查看当天创建的全部客户</div>
      </div>
    `;
  }

  /**
   * 渲染月视图日历
   * 布局：表头（月份标题 + 切换按钮）→ 星期行 → 6×7 日期网格
   * 卡片通过 absolute inset-0 填满固定高度 host，日期网格固定为 6 行。
   * @returns Lit TemplateResult
   */
  render() {
    const now = new Date();
    const isToday = (d: Date) =>
      d.getFullYear() === now.getFullYear() && d.getMonth() === now.getMonth() && d.getDate() === now.getDate();
    const cells = this.buildCells();

    return html`
      <div class="absolute inset-0 bg-surface rounded-md p-6 shadow-md flex flex-col relative">
        <crm-loading-overlay .active=${this.isLoading}></crm-loading-overlay>
        <!-- 表头：月份标题 + 月份切换 -->
        <div class="flex items-center justify-between mb-4">
          <h3 class="m-0 font-semibold text-text">${this.viewYear}年${this.viewMonth + 1}月</h3>
          <div class="flex items-center gap-1">
            <button class="w-7 h-7 flex items-center justify-center rounded hover:bg-bg cursor-pointer text-text-secondary border-0 bg-transparent transition-colors"
              aria-label="上个月" @click=${this.prevMonth}>
              <svg class="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="m15 18-6-6 6-6"/></svg>
            </button>
            <button class="px-2 py-1 text-sm rounded hover:bg-bg cursor-pointer text-text-secondary border-0 bg-transparent transition-colors"
              @click=${this.goToday}>今天</button>
            <button class="w-7 h-7 flex items-center justify-center rounded hover:bg-bg cursor-pointer text-text-secondary border-0 bg-transparent transition-colors"
              aria-label="下个月" @click=${this.nextMonth}>
              <svg class="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="m9 18 6-6-6-6"/></svg>
            </button>
          </div>
        </div>

        <!-- 星期表头（周日起始） -->
        <div class="grid grid-cols-7 mb-1">
          ${WEEKDAY_LABELS.map(w => html`<div class="text-center text-xs font-medium text-text-muted py-1">${w}</div>`)}
        </div>

        <!-- 日期网格：6 行 7 列，细线分隔，flex-1 撑满卡片剩余高度 -->
        <div class="calendar-date-grid grid grid-cols-7 divide-x divide-y divide-border border border-border rounded-md overflow-hidden">
          ${cells.map(cell => html`
            <div class="p-1 flex flex-col gap-0.5 min-h-0 overflow-hidden ${cell.inMonth ? '' : 'bg-bg/40'}">
              <!-- 日期行：日期数字（今天深红圆点）+ 当日新建客户图标 -->
              <div class="flex items-center justify-between shrink-0">
                <span class="text-xs w-5 h-5 flex items-center justify-center rounded-full
                  ${isToday(cell.date) && cell.inMonth ? 'bg-error-hover text-white font-semibold' : cell.inMonth ? 'text-text' : 'text-text-muted/50'}">
                  ${cell.date.getDate()}
                </span>
                ${cell.customers.length > 0 ? html`
                  <button class="flex items-center gap-0.5 rounded-full px-1.5 h-5 text-xs font-semibold bg-success text-white hover:bg-success-hover cursor-pointer border-0 transition-colors shadow-sm"
                    title="当日新建 ${cell.customers.length} 个客户，点击查看"
                    @mouseenter=${(e: MouseEvent) => this.showCustomerTooltip(e, cell.customers)}
                    @mouseleave=${this.hideCustomerTooltip}
                    @click=${() => this.openCustomersForDay(cell.date)}>
                    <svg class="w-3 h-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M22 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>
                    ${cell.customers.length}
                  </button>
                ` : ''}
              </div>
              <!-- 邮件条目：最多展示 3 条，超出以「+N」折叠 -->
              ${cell.items.slice(0, MAX_VISIBLE_ITEMS).map(item => html`
                <button class="w-full shrink-0 text-left text-xs truncate rounded px-1 py-0.5 bg-primary/10 text-primary hover:bg-primary/20 cursor-pointer border-0 transition-colors"
                  @mouseenter=${(e: MouseEvent) => this.showTooltip(e, item)}
                  @mouseleave=${this.hideTooltip}
                  @click=${() => this.openEmail(item)}>
                  ${item.subject || '(无主题)'}
                </button>
              `)}
              ${cell.items.length > MAX_VISIBLE_ITEMS
                ? html`<span class="text-xs text-text-muted px-1">+${cell.items.length - MAX_VISIBLE_ITEMS}</span>`
                : ''}
            </div>
          `)}
        </div>
      </div>

      <!-- 悬停浮层：标题/发件人/收件人 -->
      ${this.renderTooltip()}
      <!-- 悬停浮层：当日新建客户名单 -->
      ${this.renderCustomerTooltip()}
    `;
  }
}
